/**
 * All tvåspråkigt textinnehåll samlas här så komponenterna kan vara tunn layout.
 * `sv` = originalspråk, `en` = översättning från gamla sajten (templates/index.html).
 */

export type Pair<T = string> = { sv: T; en: T };

export const navLinks: { id: string; label: Pair }[] = [
  { id: "hero", label: { sv: "Hem", en: "Home" } },
  { id: "about", label: { sv: "Om oss", en: "About us" } },
  { id: "values", label: { sv: "Vad vi gör", en: "What we do" } },
  { id: "how", label: { sv: "Såhär funkar det", en: "How it works" } },
  { id: "team", label: { sv: "Vilka vi är", en: "Who we are" } },
  { id: "contact", label: { sv: "Kontakt", en: "Contact" } },
];

export const heroContent = {
  eyebrow: {
    sv: "AI för hälso- och sjukvård",
    en: "AI for healthcare",
  } satisfies Pair,
  title: {
    sv: "AI-analys\nför säkrare vård",
    en: "AI-based\nanalytics\nfor safer care",
  } satisfies Pair,
  subtitle: {
    sv: "Vi omvandlar fritext-avvikelser till tydlig kunskap, så att risker kan förebyggas.",
    en: "We turn free-text deviation reports into clear knowledge, so risks can be prevented.",
  } satisfies Pair,
  ctaPrimary: { sv: "Boka en demo", en: "Book a demo" } satisfies Pair,
  ctaSecondary: { sv: "Lär dig mer", en: "Learn more" } satisfies Pair,
  stats: [
    {
      value: "15 år",
      label: {
        sv: "Klinisk erfarenhet i teamet",
        en: "Clinical experience in the team",
      },
    },
    {
      value: "AI",
      label: {
        sv: "Automatisk klassificering",
        en: "Automated classification",
      },
    },
    {
      value: "GDPR",
      label: {
        sv: "Expertis kring fritextdata",
        en: "Free-text data expertise",
      },
    },
  ] as { value: string; label: Pair }[],
};

export const customersContent = {
  eyebrow: { sv: "Våra kunder", en: "Our customers" } satisfies Pair,
  title: {
    sv: "Vi samarbetar nu med Helsingborgs stad som licenskund",
    en: "We're now partnering with the City of Helsingborg as a license customer",
  } satisfies Pair,
  body: {
    sv: "Tillsammans har vi visualiserat tusentals rapporter i syfte att skapa bättre förutsättningar för att förebygga vårdskador och minska risker i vården",
    en: "Together we've visualized thousands of reports to create better conditions for preventing patient harm and reducing risks in healthcare.",
  } satisfies Pair,
  badge: {
    sv: "Licenskund sedan 2026",
    en: "License customer since 2026",
  } satisfies Pair,
  customers: [
    {
      name: "Helsingborgs stad",
      logo: "/img/helsignborg.svg",
      alt: {
        sv: "Helsingborgs stads logotyp",
        en: "City of Helsingborg logotype",
      } satisfies Pair,
    },
  ],
};

export const aboutContent = {
  eyebrow: { sv: "Om oss", en: "About us" } satisfies Pair,
  title: {
    sv: "Prisbelönad innovation med rötter från Lunds universitet",
    en: "Award-winning innovation with roots at Lund University",
  } satisfies Pair,
  body: {
    sv: "Genom preciserad information, sammanställd av det som personalen faktiskt rapporterat, kan vårdgivare sätta in mer riktade åtgärder för att förhindra vårdskador. Vårt team utgörs av forskare och erfarna personer från sjukvård och AI-utveckling som ställer höga krav på patientsäkerhet.",
    en: "With precise information compiled from what staff actually report, care providers can take more targeted action to prevent patient harm. Our team brings together researchers and experienced people from healthcare and AI development who set a high bar for patient safety.",
  } satisfies Pair,
  award: {
    sv: "Vinnare av Lunds universitets och Sparbanken Skånes pris för Framtidens innovation för social förändring 2021.",
    en: "Winner of Lund University & Sparbanken Skånes award for Future innovation for social change, 2021.",
  } satisfies Pair,
  /** Lund University innovation prize announcement (LU Innovation, 2021). */
  awardHref:
    "https://www.innovation.lu.se/artikel/har-finns-framtidens-innovationer",
  /** LU Innovation — portfolio page (Lund University) */
  lundInnovationPortfolio: {
    href: "https://www.innovation.lu.se/lu-holding/i-var-portfolj/nrativ",
    linkLabel: {
      sv: "NRATIV i LU Holdings portfölj",
      en: "NRATIV in LU Holding's portfolio",
    } satisfies Pair,
  },
};

export const valuesContent = {
  eyebrow: { sv: "Vad vi gör", en: "What we do" } satisfies Pair,
  title: {
    sv: "Vi tar moderna AI-lösningar till sjukvården",
    en: "We bring AI solutions to the front line of healthcare",
  } satisfies Pair,
  cards: [
    {
      iconKey: "shield",
      title: { sv: "Er data, stort skydd", en: "Big data, big compliance" },
      body: {
        sv: "Vi ställer höga krav på säkerhet och GDPR-efterlevnad när det gäller fritextdata. Därför är bl.a. all data som analyseras avidentifierad.",
        en: "We set high standards for security and GDPR compliance for free-text data. Among other safeguards, all data we analyze is de-identified.",
      },
    },
    {
      iconKey: "bot",
      title: {
        sv: "Automatisera de enkla sakerna",
        en: "Automate the simple things",
      },
      body: {
        sv: "Vi använder de senaste AI-teknologierna för att klassificera avvikelserapporter med hög träffsäkerhet.",
        en: "We leverage the latest AI technologies to classify text reports with high accuracy.",
      },
    },
    {
      iconKey: "insights",
      title: { sv: "Fördjupa din analys", en: "Expand your insight" },
      body: {
        sv: "Vår skräddarsydda algoritm kan sammanlänka textbaserad data på ett unikt sätt som skapar helt nya insikter.",
        en: "Our tailored algorithms cluster unstructured text data in unique ways to surface brand-new insights.",
      },
    },
  ] as {
    iconKey: "shield" | "bot" | "insights";
    title: Pair;
    body: Pair;
  }[],
};

export const howItWorksContent = {
  eyebrow: { sv: "Såhär funkar det", en: "How it works" } satisfies Pair,
  title: {
    sv: "Vårt verktyg identifierar återkommande risker i er verksamhet",
    en: "Our tool analyzes large amounts of text-based data",
  } satisfies Pair,
  steps: [
    {
      number: "01",
      title: { sv: "Händelser uppstår", en: "Incidents occur" },
      body: {
        sv: "Skador eller risker inträffar i verksamheten.",
        en: "Damage or risks occur in the workplace.",
      },
    },
    {
      number: "02",
      title: { sv: "Rapporter skrivs", en: "Reports are filed" },
      body: {
        sv: "Skadorna och riskerna dokumenteras som avvikelser eller rapporter där händelser och åtgärder beskrivs (pdf, word, excel, m.fl.).",
        en: "The injuries and risks are documented as deviations or reports describing events and actions (pdf, word, excel, etc.).",
      },
    },
    {
      number: "03",
      title: { sv: "AI analyserar", en: "AI analyzes" },
      body: {
        sv: "Vår modell analyserar automatiskt innehållet och skapar unika kategorier och statistik baserat på rapporternas innehåll.",
        en: "Our model automatically analyzes the content and creates unique categories and statistics based on the reports.",
      },
    },
    {
      number: "04",
      title: {
        sv: "Åtgärder kan sättas in",
        en: "Actions are taken",
      },
      body: {
        sv: "Trender visualiseras från textrapporter och risker kan identifieras där åtgärder behöver sättas in.",
        en: "Trends are visualized from text reports and risks are identified where action needs to be taken.",
      },
    },
  ] as { number: string; title: Pair; body: Pair }[],
};

export const teamContent = {
  eyebrow: { sv: "Vilka vi är", en: "Who we are" } satisfies Pair,
  title: {
    sv: "Träffa vårt team",
    en: "Meet our hard-working team",
  } satisfies Pair,
  members: [
    {
      photo: "/img/team/Jakob-VD-new.jpeg",
      name: "Jakob Svensson",
      role: { sv: "Co-Founder & VD", en: "Co-Founder & CEO" },
      linkedin: "https://www.linkedin.com/in/jakob-svensson-a329a9149/",
      bio: {
        sv: "Specialistsjuksköterska i psykiatrisk vård och doktor i risk och patientsäkerhet. 15 års erfarenhet från sjukvården, både kliniskt och i chefsroller. La 2023 fram sin avhandling om hur patientsäkerheten kan stärkas med nya analysmetoder.",
        en: "Specialist nurse in psychiatric care and PhD in patient risk and safety. 15 years of healthcare experience, both clinical and in management roles. In 2023 he defended his thesis on strengthening patient safety with new analytical methods.",
      },
    },
    {
      photo: "/img/team/anton-new.jpg",
      name: "Anton Danker",
      role: { sv: "Co-Founder & Utvecklare", en: "Co-Founder & Developer" },
      linkedin: "https://www.linkedin.com/in/antondanker/",
      bio: {
        sv: "Civilingenjör från KTH med MSc inom Data Science. Tidigare Chairman för KTH AI Society och Head of Internal Systems på THS Armada. Har lett utvecklingsteam som IT-konsult och arbetat som utvecklare på Nasdaq.",
        en: "Engineer from KTH with an MSc in Data Science. Former Chairman of the KTH AI Society and Head of Internal Systems at THS Armada. Has run dev teams as an IT consultant and worked as a developer at Nasdaq.",
      },
    },
    {
      photo: "/img/team/nrativ-foto-jacob.png",
      objectPosition: "center 50%",
      name: "Jacob Wirgård Wiklund",
      role: { sv: "Co-Founder & Utvecklare", en: "Co-Founder & Developer" },
      linkedin: "https://www.linkedin.com/in/jacob-wirg%C3%A5rd-wiklund/",
      bio: {
        sv: "Civilingenjör från KTH med master inom Data Science. Djup kunskap inom maskininlärning, data engineering och full-stack-utveckling. Har arbetat på Amazon och Netlight, samt drivit eget IT-konsultbolag och startup via Antler och KTH Innovation.",
        en: "Engineer from KTH with a master's in Data Science. Deep expertise in machine learning, data engineering and full-stack development. Has worked at Amazon and Netlight, and run his own IT consultancy and startup via Antler and KTH Innovation.",
      },
    },
    {
      photo: "/img/team/richard-frobell.jpeg",
      name: "Richard Frobell",
      role: { sv: "Styrelseordförande", en: "Chairman of the board" },
      linkedin: "https://www.linkedin.com/in/richard-frobell-652b9913/",
      bio: {
        sv: "25 års erfarenhet från sjukvården i Region Skåne, bland annat som forskningschef och sjukhuschef under pandemin. Forskare med 80+ vetenskapliga publikationer inom ortopedi. Adjungerad professor vid Högskolan i Halmstad och docent vid Lunds universitet.",
        en: "25 years of healthcare experience from Region Skåne, including roles as Head of Research and Hospital Director through the pandemic. Researcher with 80+ scientific publications in orthopedics. Adjunct Professor at Halmstad University and Associate Professor at Lund University.",
      },
    },
    {
      photo: "/img/team/Rickard-new.jpg",
      name: "Rickard Sjöström",
      role: { sv: "Styrelseledamot", en: "Board member" },
      linkedin: "https://www.linkedin.com/in/rickardsjostrom/",
      bio: {
        sv: "Civilekonom som arbetar som portföljförvaltare inom tech-investeringar på LU Holding. Stöttar forskningsbaserade bolag på vägen från forskning till marknad och är för närvarande engagerad i ett tiotal startups.",
        en: "Economist working as a portfolio manager in tech investments at LU Holding. Supports research-based companies on their journey from research to market and is currently engaged with about ten startups.",
      },
    },
    {
      photo: "/img/team/anna-new.jpg",
      name: "Anna Klevby Dalgaard",
      role: { sv: "Styrelseledamot", en: "Board member" },
      linkedin: "https://www.linkedin.com/in/anna-klevby-dalgaard-20318b1/",
      bio: {
        sv: "Professionell styrelseledamot som arbetar med noterade och onoterade bolag inom medicinteknik och digitala hälsolösningar. Medgrundare och tidigare ledare för Health Navigator. Tidigare managementkonsult på McKinsey & Co.",
        en: "Professional board member working with listed and unlisted companies in medtech and digital health. Co-founder and former leader of Health Navigator. Previously a management consultant at McKinsey & Co.",
      },
    },
  ] as {
    photo: string;
    objectPosition?: string;
    name: string;
    role: Pair;
    linkedin?: string;
    bio: Pair;
  }[],
};

export const contactContent = {
  eyebrow: { sv: "Kontakt", en: "Contact" } satisfies Pair,
  title: { sv: "Hör av er", en: "Let's talk" } satisfies Pair,
  kicker: {
    sv: "Boka en demo eller skicka ett mejl så återkommer vi snabbt.",
    en: "Book a demo or send us an email — we'll get back to you quickly.",
  } satisfies Pair,
  address: {
    label: { sv: "Besöksadress", en: "Address" } satisfies Pair,
    lines: ["Klippgatan 14", "116 35 Stockholm"],
  },
  email: {
    label: { sv: "Mejl", en: "Email" } satisfies Pair,
    value: "info@nrativ.se",
  },
  demoSubject: { sv: "Boka en demo", en: "Book a demo" } satisfies Pair,
};

export const demoFormContent = {
  title: { sv: "Boka en demo", en: "Book a demo" } satisfies Pair,
  subtitle: {
    sv: "Berätta lite om er verksamhet så återkommer vi inom kort.",
    en: "Tell us a little about your team and we'll get back to you shortly.",
  } satisfies Pair,
  fields: {
    name: {
      label: { sv: "Namn", en: "Name" } satisfies Pair,
      placeholder: { sv: "Förnamn Efternamn", en: "First Last" } satisfies Pair,
    },
    email: {
      label: { sv: "Mejl", en: "Email" } satisfies Pair,
      placeholder: {
        sv: "namn@organisation.se",
        en: "you@organisation.com",
      } satisfies Pair,
    },
    organization: {
      label: { sv: "Organisation", en: "Organisation" } satisfies Pair,
      placeholder: {
        sv: "T.ex. Region Skåne",
        en: "e.g. Region Skåne",
      } satisfies Pair,
    },
    phone: {
      label: {
        sv: "Telefon (valfritt)",
        en: "Phone (optional)",
      } satisfies Pair,
      placeholder: {
        sv: "+46 70 000 00 00",
        en: "+46 70 000 00 00",
      } satisfies Pair,
    },
    message: {
      label: { sv: "Meddelande", en: "Message" } satisfies Pair,
      placeholder: {
        sv: "Vad vill ni veta mer om?",
        en: "What would you like to know?",
      } satisfies Pair,
    },
  },
  submit: { sv: "Skicka förfrågan", en: "Send request" } satisfies Pair,
  sending: { sv: "Skickar…", en: "Sending…" } satisfies Pair,
  cancel: { sv: "Avbryt", en: "Cancel" } satisfies Pair,
  successTitle: {
    sv: "Tack — vi hör av oss!",
    en: "Thanks — we'll be in touch!",
  } satisfies Pair,
  successBody: {
    sv: "Vi återkommer inom kort med förslag på tid för en demo.",
    en: "We'll get back to you shortly with a time for a demo.",
  } satisfies Pair,
  successClose: { sv: "Stäng", en: "Close" } satisfies Pair,
  errorTitle: {
    sv: "Något gick fel",
    en: "Something went wrong",
  } satisfies Pair,
  errorBody: {
    sv: "Försök igen eller mejla oss direkt på info@nrativ.se.",
    en: "Please try again or email us directly at info@nrativ.se.",
  } satisfies Pair,
};

export const footerContent = {
  tagline: {
    sv: "AI-baserad analysmiljö för avvikelserapporter inom hälso- och sjukvård.",
    en: "AI-based analytics for deviation reports in healthcare.",
  } satisfies Pair,
  rights: {
    sv: "Alla rättigheter förbehållna.",
    en: "All rights reserved.",
  } satisfies Pair,
  location: {
    sv: "Stockholm, Sverige",
    en: "Stockholm, Sweden",
  } satisfies Pair,
};
